import {
  call,
  put,
  StrictEffect,
  takeEvery,
  takeLeading
} from "redux-saga/effects";

import { EntityListResponse } from "@/jenesys-types/data/crud";
import { DataType } from "@/jenesys-types/data/enums";
import { extractError } from "@/jenesys-types/errors";
import { Notification } from "@/jenesys-types/jenesys-entities/config";

import { performDataAction } from "@/services/api";
import { deleteData, listData } from "@/store/data/data.saga";

import {
  deleteAllNotifications,
  deleteAllNotificationsFailure,
  deleteNotification,
  deleteNotificationFailure,
  DeleteNotificationAction,
  getNotifications,
  GetNotificationsAction,
  getNotificationsFailure,
  getNotificationsSuccess,
  markAllNotificationsAsRead,
  markAllNotificationsAsReadFailure,
  MarkAllNotificationsAsReadAction,
  markNotificationAsRead,
  markNotificationAsReadFailure,
  MarkNotificationAsReadAction
} from "./notifications";

export function* getNotificationsSaga(
  action: GetNotificationsAction
): Generator<StrictEffect, undefined, any> {
  const { companyId } = action.payload;
  try {
    const response: EntityListResponse<Notification> = yield call(
      listData,
      DataType.Notification,
      { limit: 100, offset: 0, companyId }
    );
    yield put(getNotificationsSuccess(companyId, response));
  } catch (err) {
    yield put(getNotificationsFailure(companyId, extractError(err)));
  }
}

export function* deleteNotificationSaga(
  action: DeleteNotificationAction
): Generator<StrictEffect, undefined, any> {
  const { companyId, id } = action.payload;
  try {
    yield call(deleteData, DataType.Notification, id, { companyId });
  } catch (err) {
    yield put(deleteNotificationFailure(companyId, id, extractError(err)));
  }
}

export function* deleteAllNotificationsSaga(
  action: ReturnType<typeof deleteAllNotifications>
): Generator<StrictEffect, undefined, any> {
  const { companyId } = action.payload;
  try {
    yield call(performDataAction, DataType.Notification, "bulkDelete", {
      companyId
    });
  } catch (err) {
    yield put(deleteAllNotificationsFailure(companyId, extractError(err)));
  }
}

export function* markNotificationAsReadSaga(
  action: MarkNotificationAsReadAction
): Generator<StrictEffect, undefined, any> {
  const { companyId, id } = action.payload;
  try {
    yield call(performDataAction, DataType.Notification, "markAsRead", {
      companyId,
      id
    });
  } catch (err) {
    yield put(markNotificationAsReadFailure(companyId, id, extractError(err)));
  }
}

export function* markAllNotificationsAsReadSaga(
  action: MarkAllNotificationsAsReadAction
): Generator<StrictEffect, undefined, any> {
  const { companyId } = action.payload;
  try {
    yield call(performDataAction, DataType.Notification, "markAllAsRead", {
      companyId
    });
  } catch (err) {
    yield put(markAllNotificationsAsReadFailure(companyId, extractError(err)));
  }
}

export default function* notificationsSaga(): Generator<
  StrictEffect,
  void,
  any
> {
  yield takeLeading(getNotifications.type, getNotificationsSaga);
  yield takeEvery(deleteNotification.type, deleteNotificationSaga);
  yield takeLeading(deleteAllNotifications.type, deleteAllNotificationsSaga);
  yield takeEvery(markNotificationAsRead.type, markNotificationAsReadSaga);
  yield takeEvery(markAllNotificationsAsRead.type, markAllNotificationsAsReadSaga);
}
